import * as events from 'src/lib/events';
import * as log from 'src/lib/log';
import { captureMessage } from 'src/lib/sentry';

/**
 * Google Tag Manager event constants
 * Maps event names to their corresponding GTM event identifiers
 */
export const EVENTS = {
  userSignUp: 'user_sign_up',
  userConfirmation: 'user_confirmation',
  googleAuthFirstTime: 'google_auth_first_time',
  customerUpgradeProRequested: 'customer_upgrade_pro_requested',
  customerUpgradeAceRequested: 'customer_upgrade_ace_requested',
  firstQuery: 'first_query',
  firstCopyGeneration: 'first_copy_generation',
  completedPurchase: 'completed_purchase',
  openedCancellationPopup: 'opened_cancellation_popup',
  cancellationSurveySubmitted: 'cancellation_survey_submitted',
  openedTiersPopup: 'opened_tiers_popup',
  clickedPlanBuyNowButton: 'clicked_plan_buy_now_button',
  completedTierUpgrade: 'completed_tier_upgrade_purchase',
  completedOveragePurchase: 'completed_overage_purchase',
  userTrack: 'user_track',
  projectCreated: 'project_created',
  onboardingCompleted: 'onboarding_completed',
  systemError401: 'system.error_401',
  systemError500: 'system.error_500',
  systemSentryEvent: 'system.sentry_event',
  aiQueryMade: 'ai_query_made',
  rephraseQueryMade: 'rephrase_query_made',
  assetQueryMadePostfix: '_query_made',
  firstIntegration: 'first_integration',
  secondTargetAudience: '2_target_audiences',
  cipAccountsConnected: 'cip_accounts_connected',
  clickedVariationDetails: 'project_variation_clicked_details',
  clickedRecomposeDetails: 'project_variation_project_variation_clicked_details_recompose',
  addedTalkingPoints: 'document_talking_points_added',

  // Deprecated
  // copySavedVariation: 'copy_saved_variation',
  // copySavedVariationPostfix: '_copy_saved_variation',
} as const;

/**
 * Type for GTM event names
 */
export type GtmEventName = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * Type for GTM event attributes
 */
export type GtmEventAttributes = Record<string, unknown> | null;

/**
 * Type for GTM event object
 */
export interface GtmEvent {
  event: GtmEventName;
  [key: string]: unknown;
}

/**
 * Flag to track if GTM not loaded has been reported
 */
let reportedNotLoaded = false;

/**
 * Reports that GTM is not loaded to the backend
 * Only reports once per session to avoid spam
 * @param eventName - The name of the event that couldn't be tracked
 * @param attributes - The attributes that were supposed to be sent
 */
const reportNotLoaded = (eventName: GtmEventName, attributes: GtmEventAttributes): void => {
  if (reportedNotLoaded) {
    return;
  }

  // Report this event only once per session
  reportedNotLoaded = true;

  log.warning('GTM is not loaded');
  events.trackUserEventNoAuth(events.SYSTEM.gtmInaccessible, {
    eventName,
    attributes,
  });
};

/**
 * Pushes an event to the Google Tag Manager data layer
 * @param eventName - The name of the GTM event to fire
 * @param attributes - Optional attributes to include with the event
 *
 * @example
 * ```typescript
 * // Fire a simple event
 * pushDataLayerEvent(EVENTS.userSignUp);
 *
 * // Fire an event with attributes
 * pushDataLayerEvent(EVENTS.completedPurchase, {
 *   value: 99.99,
 *   currency: 'USD',
 *   plan: 'pro'
 * });
 * ```
 */
export function pushDataLayerEvent(
  eventName: GtmEventName,
  attributes: GtmEventAttributes = null
): void {
  // If GTM lib is not loaded - report to BE
  if (!window.google_tag_manager) {
    reportNotLoaded(eventName, attributes);
    return;
  }

  const event: GtmEvent = {
    ...attributes,
    event: eventName,
  };

  try {
    window.dataLayer?.push(event);
  } catch (error) {
    // In case or error in GTM event - report "warning" to Sentry
    captureMessage('Error while firing GTM event', {
      level: 'warning',
      extra: {
        error: String(error),
        source: 'lib/gtm.js -> pushDataLayerEvent',
        eventName,
        attributes,
      },
    });
  }
}
